import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFocusEffect } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import { logOffersDisplayed } from 'features/offer/analytics/log-offers-displayed';
import {
  findOfferListItemIndex,
  offerListPositionParams,
} from 'features/offer/analytics/offer-list-position';
import { isEmpty } from 'lodash-es';
import { FlatList, StyleSheet, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import ConfettiFriesAnimation from 'components/lottie-animations/confetti-fries';
import { useConfettiFriesAnimation } from 'components/lottie-animations/confetti-fries/use-confetti-fries-animation';
import {
  PullToReloadScrollView,
  pullToReloadStyles,
  usePullToReload,
} from 'components/pull-to-reload/pull-to-reload';
import { toaster } from 'components/toaster/toaster';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import {
  ClickEventComponentNames,
  CustomEventNames,
  logButtonClick,
  logLoyaltyEvent,
} from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyOfferWithInlineAlertMessage } from 'state/loyalty/hooks/types';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { pushUnavailableOffersToBottom } from 'state/loyalty/hooks/utils/helpers';
import {
  getWalmartSeasonalConfigOfferIds,
  pushWalmartNewHalloween25OfferToTop,
  pushWalmartOffersToTop,
  pushWalmartQuarterlyWhooperToBottom,
} from 'state/loyalty/hooks/utils/walmart';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import {
  AppOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyOffer,
  SanityConfigOffer,
  buildAppPersonalizedOffer,
  isAppOffer,
} from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { spacing } from 'styles/constants';
import { TRANSITION_ANIMATION_TIME_MS, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';
import { waitForDuration } from 'utils/wait-for-duration';

import { useLoyaltyWalmart } from '../loyalty-partners/walmart/use-loyalty-walmart';

import OffersEmptyScreen from './empty-screen/empty-screen';
import EndOfOffers from './end-of-offers/loyalty-end-of-offers';
import { LoyaltyListItem } from './loyalty-list-item';
import { LoyaltyOffersSkeletonLoader } from './loyalty-offers-skeleton-loader';
import { LoyaltyPromoCodeCTA } from './loyalty-promo-code/loyalty-promo-code-cta';
import { LoyaltyPromoCodeDrawer } from './loyalty-promo-code/loyalty-promo-code-drawer';
import { PromoCodeRedeemSuccessParam } from './loyalty-promo-code/types';
import { OffersInCart } from './offers-in-cart/offers-in-cart';
import { useGetIncentive } from './use-get-incentive';
import { useLoyaltyBaseOffers } from './use-loyalty-base-offers';
import useLoyaltyOffersFlow from './use-loyalty-offers-flow';

export const LoyaltyOffersBase = () => {
  const { loyaltyStandardOffersEnabled } = useLoyaltyContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { setModalAuthState } = useAuthContext();
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const dispatch = useAppDispatch();
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();
  const [authRequested, setAuthRequested] = useState(false);

  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const showWalmartSeasonalPromo = useFlag(LaunchDarklyFlag.SHOW_WALMART_SEASONAL_PROMO);
  const { removeFromCart } = useCart();
  const { removeItemFromMinimalCart } = useMinimalCart();

  const router = useRouter();

  const { loyaltyEngineId } = useLoyaltyOffersFlow();
  const skipAnimation = useRef(true);

  const { triggerAnimation } = useConfettiFriesAnimation();
  const queryParams = useLocalSearchParams<{ success?: string; promoCode?: string }>();
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(
    !!stringToBool(queryParams.success)
  );

  const [isPromoCodeOpen, _setIsPromoCodeOpen] = useState(false);

  const openPromoCodeDrawer = useCallback(() => {
    setTimeout(() => {
      _setIsPromoCodeOpen(true);
    }, TRANSITION_ANIMATION_TIME_MS);
  }, []);

  const closePromoCodeDrawer = useCallback(() => {
    _setIsPromoCodeOpen(false);
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (queryParams.promoCode && !authRequested) {
        openPromoCodeDrawer();
      }
    }, [openPromoCodeDrawer, queryParams.promoCode, authRequested])
  );

  const shouldDisplayPromoCodeDrawer = useRef(false);

  useLoyaltyUserState({
    refreshOnMountOrFocus: true,
    onSuccess: () => {
      if (shouldDisplayPromoCodeDrawer.current) {
        openPromoCodeDrawer();
        shouldDisplayPromoCodeDrawer.current = false;
      }
    },
  });

  const onDismissPromoDrawer = useCallback(() => {
    router.setParams({ promoCode: '' });
    closePromoCodeDrawer();
  }, [closePromoCodeDrawer, router]);

  const handleAuthenticationRequired = (promoCode: string) => {
    router.setParams({ promoCode });
    setAuthRequested(true);
    closePromoCodeDrawer();

    // Timeouts are required to ensure that each drawer's closing animation ends before the
    // other drawer appears. Otherwise, the drawer that appears will, at least on Android, remain fixed in the middle of
    // the screen vertically.
    setTimeout(() => {
      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        trigger: ModalAuthTrigger.Global,
        onClose: () => {
          setAuthRequested(false);
          openPromoCodeDrawer();
        },
        // Despite the name, it's also called after a successful signup.
        onSignInSuccess: () => {
          // We want to display the promo code drawer again only after loyalty data is fetched.
          // Otherwise, it pops up, then loading starts and it's hidden and then it pops up again.
          // We also don't want to trigger a re-render as it's visually noticeable, hence the use of ref.
          shouldDisplayPromoCodeDrawer.current = true;
        },
      });
    });
  };

  const {
    offerListItems,
    offersInCart,
    reprocessOffers,
    refreshOffers,
    loading: loyaltyOffersLoading,
    baseOffersLoading,
  } = useLoyaltyBaseOffers();

  const { selectedIncentive } = useGetIncentive({ loyaltyEngineId });

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { isMenuLoading: isLegacyMenuLoading } = useMenuContext();
  const { isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();
  const menuLoading = isUnifiedMenuEnabled ? isUnifiedMenuLoading : isLegacyMenuLoading;

  const { data: loyaltyWalmart, loading: isLoyaltyWalmartLoading } = useLoyaltyWalmart();

  // @TODO: Review how we handle the different loading states before displaying the offer list
  const offersAreLoading = loyaltyOffersLoading || menuLoading || isLoyaltyWalmartLoading;

  // TODO: extract this method to a reusable place
  const removeLoyaltyOffer = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const appliedOfferToRemove = appliedOffers.find(
        offer => offer.id === offerToRemove.loyaltyEngineId
      );

      if (appliedOfferToRemove?.cartId) {
        // Remove applied offer...
        dispatch(actions.loyalty.removeAppliedOffer(appliedOfferToRemove));
        // Remove it from the cart...
        removeFromCart({ cartId: appliedOfferToRemove.cartId });
        return true;
      }

      return false;
    },
    [appliedOffers, dispatch, removeFromCart]
  );

  const handleRemove = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
        cartEntry =>
          cartEntry.type === ICartEntryType.OFFER &&
          cartEntry.details.offer._id === offerToRemove._id
      );

      let showNotification = false;

      if (redemptionOfferToRemove) {
        removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
        showNotification = true;
      } else if (isDiscountLoyaltyOffer(offerToRemove)) {
        if (isUnifiedMenuEnabled) {
          removeItemFromMinimalCart({ cartId: DISCOUNT_OFFER_CART_ID } as ICartEntry);
        }
        removeFromCart({ cartId: DISCOUNT_OFFER_CART_ID });
        dispatch(actions.loyalty.setSelectedOffer(null));
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        reprocessOffers();
        showNotification = true;
      } else {
        reprocessOffers();
        showNotification = removeLoyaltyOffer(offerToRemove);
      }

      if (showNotification) {
        toaster.positive(t('offerAppliedSuccess'));
      }
    },
    [
      inRestaurantRedemptionCart,
      removeInRestaurantRedemptionEntry,
      isUnifiedMenuEnabled,
      removeFromCart,
      dispatch,
      reprocessOffers,
      removeItemFromMinimalCart,
      removeLoyaltyOffer,
    ]
  );

  // Refetch offers if shouldRefetchOffers is true or an offer is added to cart
  // also responsible for initial loading of offers
  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  // Refetch offers if an offer is added to cart
  useEffect(() => {
    if (offersInCart?.length && !offerListItems.length) {
      dispatch(actions.loyalty.setShouldRefetchOffers(true));
    }
  }, [offersInCart?.length, offerListItems.length, dispatch]);

  const sortedOfferListItems = useMemo(() => {
    if (!offerListItems?.length) {
      return null;
    }
    const mergedOffers = [...offerListItems];
    const walmartSeasonalOffers = getWalmartSeasonalConfigOfferIds(loyaltyWalmart);

    mergedOffers.sort(
      pushWalmartOffersToTop(showWalmartSeasonalPromo ? walmartSeasonalOffers : [])
    );
    mergedOffers.sort(pushWalmartNewHalloween25OfferToTop);

    if (showWalmartSeasonalPromo) {
      // Per Walmart request, we want to put Free Whooper offer at the bottom when seasonal promo is enabled
      mergedOffers.sort(pushWalmartQuarterlyWhooperToBottom);
    }

    mergedOffers.sort(pushUnavailableOffersToBottom);
    return mergedOffers;
  }, [offerListItems, loyaltyWalmart, showWalmartSeasonalPromo]);

  const handleOfferPress = useCallback(
    (incentive: LoyaltyOfferWithInlineAlertMessage) => {
      if (incentive.inlineAlertMessage) {
        return;
      }
      const incentiveId = incentive._id;
      const loyaltyEngineId = incentive.loyaltyEngineId;
      if (incentiveId !== selectedIncentive?._id) {
        logLoyaltyEvent({
          name: CustomEventNames.OFFER_SELECTED,
          incentives: [incentive],
          attributes: {
            serviceMode: incentive.offerServiceMode,
          },
        });

        const offerIndex = findOfferListItemIndex(sortedOfferListItems ?? [], {
          kind: 'offer',
          loyaltyEngineId: loyaltyEngineId ?? '',
        });

        router.navigate({
          pathname: '/offers/[loyaltyEngineId]',
          params: {
            loyaltyEngineId,
            ...offerListPositionParams(offerIndex),
          },
        });
      }
    },
    [router, selectedIncentive, sortedOfferListItems]
  );

  const handleGroupPress = useCallback(
    (offerGroup: { cmsId: string; id?: string }) => {
      const groupIndex = findOfferListItemIndex(sortedOfferListItems ?? [], {
        kind: 'group',
        cmsId: offerGroup.cmsId,
        id: offerGroup.id,
      });

      router.navigate({
        pathname: '/offers/group/[offerGroupId]',
        params: {
          offerGroupId: offerGroup.cmsId,
          ...(offerGroup.id ? { loyaltyEngineId: offerGroup.id } : {}),
          ...offerListPositionParams(groupIndex),
        },
      });
    },
    [router, sortedOfferListItems]
  );

  // Log event when offers are displayed
  useEffect(() => {
    if (offerListItems.length > 0 && !offersAreLoading) {
      logOffersDisplayed(offerListItems);
    }
  }, [offerListItems, offersAreLoading]);

  useEffect(() => {
    if (
      loyaltyEngineId &&
      !selectedIncentive &&
      loyaltyStandardOffersEnabled &&
      !baseOffersLoading &&
      !isEmpty(offerListItems)
    ) {
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
  }, [
    offerListItems,
    baseOffersLoading,
    selectedIncentive,
    loyaltyEngineId,
    loyaltyStandardOffersEnabled,
  ]);

  const renderItem = useCallback(
    ({ item }: { item: AppOfferListItem }) => {
      return (
        <LoyaltyListItem
          item={item}
          handleOfferPress={handleOfferPress}
          handleGroupPress={handleGroupPress}
          skipAnimation={skipAnimation.current}
        />
      );
    },
    [handleOfferPress, handleGroupPress]
  );

  const onPromoCodeRedeemSuccess = useCallback(
    ({ configOffer, engineOffer }: PromoCodeRedeemSuccessParam) => {
      skipAnimation.current = false;
      toaster.positive(t('promoCodeAddedToOffers'));
      dispatch(actions.loyalty.setCmsOffers([configOffer as SanityConfigOffer]));
      const appPersonalizedOffer = buildAppPersonalizedOffer(
        configOffer as SanityConfigOffer,
        engineOffer as EnginePersonalizedOffer
      );
      dispatch(actions.loyalty.unshiftAppPersonalizedOffer(appPersonalizedOffer));

      router.navigate(
        routes.loyaltyOfferDetails.replace(':loyaltyEngineId', appPersonalizedOffer.loyaltyEngineId)
      );
    },
    [dispatch, router]
  );

  const canShowPromoCode = enableLoyaltyPromoCodes && !selectedIncentive;

  const ListHeaderComponent = useCallback(() => {
    const onOpenPromoDrawer = () => {
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PROMO_CODE_TILE,
          name: 'Have a Promo Code?',
        },
      });
      openPromoCodeDrawer();
    };

    return (
      <>
        {canShowPromoCode && <LoyaltyPromoCodeCTA onPress={onOpenPromoDrawer} />}
        {offersInCart?.length ? (
          <OffersInCart
            offers={offersInCart}
            handlePress={handleOfferPress}
            handleRemove={handleRemove}
          />
        ) : null}
      </>
    );
  }, [canShowPromoCode, handleOfferPress, handleRemove, offersInCart, openPromoCodeDrawer]);

  const onRefresh = async () => {
    await waitForDuration(1700);
    refreshOffers();
  };

  const { pullToReloadSpinner, pullToReloadProps } = usePullToReload({
    progressViewOffset: 10,
    onRefresh,
  });
  const nativeTabBarContentInset = useNativeTabBarContentInset();

  // Trigger confetti fries animation and W+ toast message based on `success` query parameter
  useEffect(() => {
    if (showSuccessAnimation && loyaltyStandardOffersEnabled && !offersAreLoading) {
      triggerAnimation();
      toaster.positive(t('walmartEnjoyYourOffers'));
      setShowSuccessAnimation(false);
      router.setParams({ success: undefined });
    }
  }, [
    loyaltyStandardOffersEnabled,
    router,
    showSuccessAnimation,
    offersAreLoading,
    triggerAnimation,
  ]);

  if (!loyaltyStandardOffersEnabled) {
    // TODO: show empty state if Loyalty is not enabled
    return null;
  }

  if (offersAreLoading) {
    return <LoyaltyOffersSkeletonLoader />;
  }

  return (
    <>
      {sortedOfferListItems?.length ? (
        <>
          <View style={pullToReloadStyles.scrollViewContainer}>
            {pullToReloadSpinner}
            <FlatList
              scrollEventThrottle={16}
              ListHeaderComponent={ListHeaderComponent}
              ListFooterComponent={EndOfOffers}
              contentContainerStyle={[
                styles.flatList,
                nativeTabBarContentInset ? { paddingBottom: nativeTabBarContentInset } : null,
              ]}
              data={sortedOfferListItems}
              initialNumToRender={5}
              keyExtractor={(item, index) => {
                if (isAppOffer(item)) {
                  return item.loyaltyEngineId;
                }
                // Groups (systemwide + personalized) always have a cmsId; use as stable key.
                if ('cmsId' in item && item.cmsId) {
                  return item.cmsId;
                }
                // Fallback for safety.
                return `${index}`;
              }}
              renderItem={renderItem}
              {...pullToReloadProps}
            />
          </View>
          <ConfettiFriesAnimation />
        </>
      ) : (
        <PullToReloadScrollView onRefresh={onRefresh} progressViewOffset={10}>
          <OffersEmptyScreen />
        </PullToReloadScrollView>
      )}
      {isPromoCodeOpen && (
        <LoyaltyPromoCodeDrawer
          isOpen={isPromoCodeOpen}
          onDismiss={onDismissPromoDrawer}
          promoCode={queryParams.promoCode?.toString()}
          onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
          onAuthenticationRequired={handleAuthenticationRequired}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  header: { marginTop: spacing.$2, maxWidth: WEB_SMALL_MAX_WIDTH },
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
